import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import {
  Layout,
  Page,
  Card,
  Text,
  BlockStack,
  TextContainer,
  Button,
  InlineStack,
  Spinner,
  Tabs,
} from "@shopify/polaris";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import {
  fetchUserDetails,
  setDestroy,
} from "containers/user/user-container.slice";
import {
  getAuthCodeUrl,
  disconnectMicrosoftSync,
} from "services/user-select.service";
import FilesCategoriesTree from "containers/document/category-permissions-tree/category-tree";
import Customers from "containers/customers-access-list/customers";
import ExcludedCustomers from "containers/customers-hide-list/customers";
import { fetchOnboardingsByUser } from "services/onboarding.service";
import { STEP_LABELS } from "utils/onboardingStatus";
import PermissionChips from "components/permission-picker/PermissionChips";
import { getOnboardingStatusLabel } from "utils/onboardingStatus";
import { API_URL } from "../../constants";

function UserContainer() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { userId, companyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const loadUserData = useCallback(() => {
    if (userId && companyId) {
      dispatch(fetchUserDetails({ userId, companyId }));
    }
  }, [userId, companyId, dispatch]);

  const { userEntity, isLoading, isError, errMsg } = useSelector(
    (state: RootState) => state.userContainerReducer
  );

  const { role, session } = useSelector(
    (state: RootState) => state.authReducer
  );

  // Check if current user is super admin (Master)
  const isSuperAdmin = role?.permissionsgroups?.includes("Master");

  useEffect(() => {
    loadUserData();
    return () => {
      dispatch(setDestroy());
    };
  }, [loadUserData]);

  // Connect/disconnect act on the signed-in user's own mailbox, so they belong
  // only on the signed-in user's own card.
  const isOwnCard = !!session?.id && session.id === userEntity?.id;
  // The server sends other people's rights only to a Master; for anyone else
  // the list is empty, which must not read as "no rights".
  const canSeeRights = isSuperAdmin || isOwnCard;

  const handleOnFileChange = () => {};

  const handleEditClick = (id: string) => {
    dispatch(setModalxComponent("UserForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handleEditEmailClick = (id: string) => {
    dispatch(setModalxComponent("EditEmailForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handleAuthCodeUrl = async (id: string) => {
    const authCodeUrl = await getAuthCodeUrl(companyId);
    // Redirect the user to the generated URL
    window.location.href = authCodeUrl;
  };

  const handleDisconnect = async (id: string) => {
    await disconnectMicrosoftSync(companyId);
    loadUserData();
  };

  // Define tab paths
  // "/files",
  const tabPaths = [
    "/category-permission",
    "/customer-permission",
    "/customer-hide",
    "/onboarding-documents",
  ];
  const currentPath = location.pathname.split("/").pop();
  const initialSelectedTab =
    tabPaths.indexOf(`/${currentPath}`) !== -1
      ? tabPaths.indexOf(`/${currentPath}`)
      : 0;
  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);

  if (isLoading && !userEntity) {
    return (
      <Page fullWidth title={t("loading", { defaultValue: "Loading..." })}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "100px 0",
          }}
        >
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  if (!userEntity) {
    return (
      <Page
        fullWidth
        title={t("user_load_failed", { defaultValue: "Unable to load user" })}
      >
        <Card roundedAbove="sm">
          <Text as="p" variant="bodyMd" tone="critical">
            {isError
              ? t(errMsg || "not_fetch")
              : t("user_not_found", { defaultValue: "User not found" })}
          </Text>
        </Card>
      </Page>
    );
  }

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
    navigate(`/app/${companyId}/users/${userId}${tabPaths[selectedTabIndex]}`, {
      replace: true,
    });
  };

  const tabs = [
    // { id: "files", content: t("files") },
    { id: "category-permission", content: t("category_permission") },
    { id: "customer-permission", content: t("customer_permission") },
    { id: "customer-hide", content: t("customer_hide") },
    { id: "onboarding-documents", content: t("onboarding_documents") },
  ];

  return (
    <Page
      fullWidth
      backAction={{ content: "User-page", url: `/app/${companyId}/users` }}
      title={userEntity.name}
      titleMetadata={isLoading && <Spinner size="small" />}
    >
      <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
      <Layout>
        <Layout.Section>
          <BlockStack gap="500">
            {selectedTab === 0 && <FilesCategoriesTree />}
            {/* {selectedTab === 0 && (
              <Card roundedAbove="sm">
                <div className="flex">
                  <Text as="h2" variant="headingSm">
                    {t("files")}
                  </Text>
                  <div className="flex-grow"></div>
                  <ButtonGroup>
                    <UploadFile
                      onChange={handleOnFileChange}
                      subject="user"
                      subjectId={userEntity.id}
                      categoryType={"internal"}
                      isUserSelectorVisible={true}
                    />
                  </ButtonGroup>
                </div>
                {userEntity.files.length > 0 ? (
                  <Files files={userEntity.files} subject="user" />
                ) : (
                  <EmptyState
                    heading={t("no_files")}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <UploadFile
                      onChange={handleOnFileChange}
                      subject="user"
                      subjectId={userEntity.id}
                      categoryType={"internal"}
                      isUserSelectorVisible={true}
                    />
                  </EmptyState>
                )}
              </Card>
            )} */}
            {selectedTab === 1 && <Customers />}
            {selectedTab === 2 && <ExcludedCustomers />}
            {selectedTab === 3 && (
              <OnboardingDocuments companyId={companyId!} userId={userId!} />
            )}
          </BlockStack>
        </Layout.Section>
        {selectedTab === 0 && (
          <Layout.Section variant="oneThird">
            <BlockStack gap="500">
              <Card roundedAbove="sm">
                <InlineStack align="space-between">
                  <Button
                    variant="plain"
                    onClick={() => handleEditClick(userEntity.id)}
                    accessibilityLabel="Preview"
                  >
                    {t("edit")}
                  </Button>
                  {isSuperAdmin && (
                    <Button
                      variant="plain"
                      size="slim"
                      onClick={() => handleEditEmailClick(userEntity.id)}
                    >
                      {t("edit_email")}
                    </Button>
                  )}
                </InlineStack>
                <TextContainer>
                  <Text as="p" variant="bodyMd">
                    <strong>{t("Email")}:</strong> {userEntity.email}
                  </Text>
                  {userEntity.name ? (
                    <Text as="p" variant="bodyMd">
                      <strong>{t("name")}:</strong> {userEntity.name}
                    </Text>
                  ) : (
                    <Text as="p" variant="bodyMd">
                      {t("no_name_provided")}
                    </Text>
                  )}
                  {userEntity.phone ? (
                    <Text as="p" variant="bodyMd">
                      <strong>{t("phone")}:</strong> {userEntity.phone}
                    </Text>
                  ) : (
                    <Text as="p" variant="bodyMd">
                      {t("no_phone_number_provided")}
                    </Text>
                  )}
                  {userEntity.externalCode ? (
                    <Text as="p" variant="bodyMd">
                      <strong>{t("external_code")}:</strong>{" "}
                      {userEntity.externalCode}
                    </Text>
                  ) : (
                    <Text as="p" variant="bodyMd">
                      {t("no_external_code_provided")}
                    </Text>
                  )}
                  {userEntity?.manager ? (
                    <Text as="p" variant="bodyMd">
                      <strong>{t("manager")}:</strong>{" "}
                      {userEntity?.manager?.name}
                    </Text>
                  ) : (
                    <Text as="p" variant="bodyMd">
                      {t("no_manager_provided")}
                    </Text>
                  )}
                  {canSeeRights &&
                    (userEntity.permissions.length > 0 ? (
                      <>
                        <Text as="p" variant="bodyMd">
                          <strong>{t("user_permissions")}:</strong>
                        </Text>
                        <PermissionChips permissions={userEntity.permissions} />
                      </>
                    ) : (
                      <Text as="p" variant="bodyMd">
                        {t("no_user_permissions_provided")}
                      </Text>
                    ))}
                </TextContainer>
              </Card>

              <Card roundedAbove="sm">
                <Text as="p" variant="bodyMd">
                  <strong>{t("email_connect")}:</strong> {userEntity.email}
                </Text>

                <Text as="p" variant="bodyMd">
                  <strong>{t("status")}:</strong>{" "}
                  {userEntity?.emailSync?.microsoft?.isConnected
                    ? t("connected")
                    : t("not_connected")}
                </Text>
                {!isOwnCard && !userEntity?.emailSync?.microsoft?.isConnected && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("outlook_connect_by_employee_hint")}
                  </Text>
                )}
                {isOwnCard && !userEntity?.emailSync?.microsoft?.isConnected && (
                  <InlineStack align="center">
                    <Button
                      size="large"
                      onClick={() => handleAuthCodeUrl(userEntity.id)}
                    >
                      {t("connect")}
                    </Button>
                  </InlineStack>
                )}
                {isOwnCard && userEntity?.emailSync?.microsoft?.isConnected && (
                  <InlineStack align="center">
                    <Button
                      size="large"
                      onClick={() => handleDisconnect(userEntity.id)}
                    >
                      {t("disconnect")}
                    </Button>
                  </InlineStack>
                )}
              </Card>
            </BlockStack>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}

/** Onboarding documents list for user profile */
function OnboardingDocuments({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) {
  const dispatch: AppDispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const [onboardings, setOnboardings] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchOnboardingsByUser(companyId, userId);
        setOnboardings(data || []);
      } catch {
        setOnboardings([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, userId]);

  if (loading) {
    return (
      <Card roundedAbove="sm">
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Spinner size="large" />
        </div>
      </Card>
    );
  }

  if (onboardings.length === 0) {
    return (
      <Card roundedAbove="sm">
        <Text as="p" variant="bodyMd">
          {t("no_onboarding_documents")}
        </Text>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      {onboardings.map((ob: any) => (
        <Card roundedAbove="sm" key={ob.id}>
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <Text as="h2" variant="headingSm">
                {ob.firstName} {ob.lastName} —{" "}
                {getOnboardingStatusLabel(ob.status, t, lang)}
              </Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {new Date(ob.createdAt).toLocaleDateString("lt-LT")}
              </Text>
            </InlineStack>

            {ob.documents.length === 0 ? (
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("no_documents")}
              </Text>
            ) : (
              <BlockStack gap="200">
                {ob.documents.map((doc: any) => {
                  const stepKey = doc.fileType?.replace("_pdf", "");
                  const stepLabel =
                    STEP_LABELS[stepKey]?.[lang] ||
                    STEP_LABELS[stepKey]?.en ||
                    doc.fileType;

                  return (
                    <InlineStack key={doc.id} gap="300" blockAlign="center">
                      <Text as="span" variant="bodyMd">
                        {stepLabel}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {doc.file.name || doc.file.title}
                      </Text>
                      {doc.file.isSigned && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: "9999px",
                            fontSize: "12px",
                            fontWeight: 500,
                            backgroundColor: "#dcfce7",
                            color: "#166534",
                          }}
                        >
                          {t("signed")}
                        </span>
                      )}
                      <Button
                        size="slim"
                        onClick={() => {
                          dispatch(setModalxComponent("PdfView"));
                          dispatch(
                            setModalxComponentProps({
                              id: doc.file.id,
                              companyId,
                            })
                          );
                        }}
                      >
                        {t("view_pdf")}
                      </Button>
                      <Button
                        size="slim"
                        onClick={() =>
                          window.open(
                            `${API_URL}/${companyId}/files/download/${doc.file.id}`,
                            "_blank"
                          )
                        }
                      >
                        {t("download")}
                      </Button>
                    </InlineStack>
                  );
                })}
              </BlockStack>
            )}
          </BlockStack>
        </Card>
      ))}
    </BlockStack>
  );
}

export default UserContainer;
